/* freegift-modal.js — Dr Free Gift (docapp) integration
   --------------------------------------------------------------------------
   Layered on top of the "Dr Free Gift, BOGO" app (class prefix `docapp-`),
   which OWNS the free-gift logic (0 € pricing, 35 € threshold, "1 max").
   This script never touches that logic — it only:

   1) FOUC GUARD: the popup is hidden by default (`html.fg-guard`, armed from the
      <head> by freegift-modal-style.liquid) and revealed only once the open is
      known to be legitimate. Without it the app's automatic open painted before
      we could react, flashing the popup on every add-to-cart and refresh.
      See the FOUC guard block below for the full reveal logic.

   2) AUTO-PICK: when the app's gift-choice popup opens *automatically* (cart
      crossed the threshold) and nothing is selected yet, it leaves the popup
      invisible (guard stays armed), clicks the first sample's own "Ajouter"
      button — so the app adds it for free, exactly as if the customer had — then
      closes it. Result: a default gift is always present, so the swap trigger
      always exists. It deliberately does NOT fire when the customer opens the
      popup themselves to choose/swap.

   3) TRIGGER: binds the "Changer" link that snippets/cart-line-items.liquid
      server-renders under the first SAMPLE line ([data-freegift-open]) so it
      opens the popup (by clicking the app's `.docapp-variant-options-trigger`).
      Delegated on document, so the drawer's Section-Rendering re-render can
      replace the markup freely — nothing to re-inject, nothing to re-place.

   4) NUDGE: dispatches a `resize` when the popup opens so the app's Siema slider
      recomputes its slide widths for our restyled (narrower) dialog.

   5) PRICE GUARD: the popup's whole LOOK is CSS (freegift-modal-style.liquid),
      which survives the app's re-renders. The one thing CSS can't reliably win
      is the gift-price colour if the app sets it inline — so we force just that,
      idempotently (a one-shot per element, re-applied to freshly-rendered ones).

   No-ops gracefully whenever the app or its markup is absent. */
(function () {
  'use strict';

  var script = document.querySelector('script[src*="freegift-modal"]');
  var ds = (script && script.dataset) || {};
  var AUTOPICK = ds.fgAutopick !== 'false';
  var RESTYLE = ds.fgRestyle === 'true';   // when off, the popup keeps the app's base design
  var ACCENT = ds.fgAccent || '#1c1c1c';

  function nowMs() { try { return Date.now(); } catch (_) { return 0; } }

  /* ---------- open the app's gift-choice popup ---------- */

  function nativeTrigger() {
    return document.querySelector('.docapp-variant-options-trigger')
        || document.querySelector('.docapp-variant-options-trigger-container [docapp-data-trigger]')
        || document.querySelector('.docapp-free-gift-variant-select');
  }
  function clickNativeTrigger() {
    var t = nativeTrigger();
    if (t) { t.click(); return true; }
    return false;
  }
  function showExistingOverlay() {
    var ov = document.querySelector('.docapp-overlay-modal');
    if (!ov) return false;
    ov.style.display = '';
    ov.style.visibility = 'visible';
    ov.removeAttribute('hidden');
    var wrap = ov.querySelector('#docapp-gift-choice-modal-wrapper');
    if (wrap) { wrap.classList.add('docapp-visible'); }
    return true;
  }
  function openModal(e) {
    if (e) { e.preventDefault(); }
    markUserOpen();
    if (clickNativeTrigger()) { nudgeSlider(); return; }
    if (showExistingOverlay()) { nudgeSlider(); return; }
  }

  /* ---------- FOUC guard ----------------------------------------------------
     The app owns when its popup opens, and it opens one AUTOMATICALLY as soon as
     the cart crosses the gift threshold (and again on some page loads). We used to
     hide it reactively — MutationObserver, 50ms debounce, then add the hide class —
     which is exactly one frame too late: the popup painted first, so you saw it
     flash in and vanish on every add-to-cart and refresh.

     Inverted: the popup is hidden by DEFAULT (`html.fg-guard`, armed by the inline
     boot script in freegift-modal-style.liquid before the app's own script can run),
     and it is REVEALED only once we know the open is legitimate:
       · a click on our link / the app's native trigger → disarmed synchronously in
         the capture phase, i.e. before the app even opens it → no delay, no flash;
       · any other open we didn't predict → revealed from onModalOpened (~50ms), a
         short reveal delay instead of a flash;
       · an auto-open with nothing selected → stays armed, auto-pick runs invisibly.
     Fail-open: the guard only exists while THIS script is alive to lift it, and the
     boot script self-heals on a timer if the script never loads at all. */
  var GUARD = 'fg-guard';
  function arm() {
    document.documentElement.classList.add(GUARD);
  }
  function disarm() {
    document.documentElement.classList.remove(GUARD);
  }

  /* ---------- user-vs-auto open detection ----------
     Any click on our link or the app's native trigger flags the *next* popup
     opening as user-initiated, so auto-pick stays out of the way for choose/swap
     — and lifts the guard right away so the popup appears instantly. */
  var userOpenedAt = 0;
  function markUserOpen() { userOpenedAt = nowMs(); disarm(); }
  function recentUserOpen() { return (nowMs() - userOpenedAt) < 4000; }
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.docapp-variant-options-trigger, .docapp-variant-options-trigger-container, .cart-freegift-trigger')) {
      markUserOpen();
    }
  }, true);

  /* ---------- Siema resize nudge (restyle only — that's when we narrow the dialog) ---------- */
  function nudgeSlider() {
    if (!RESTYLE) return;
    var fire = function () { try { window.dispatchEvent(new Event('resize')); } catch (_) {} };
    fire();
    setTimeout(fire, 80);
    setTimeout(fire, 280);
    setTimeout(fire, 600);
  }

  /* ---------- the one property CSS can't always win: the gift-price colour ----------
     Idempotent: tag each price span once, so re-running (mutations / re-renders) only
     touches freshly-rendered spans — no observer loop. Everything else is pure CSS. */
  function enforcePrice() {
    if (!RESTYLE) return;
    var modal = document.querySelector('.docapp-overlay-modal');
    if (!modal) return;
    modal.querySelectorAll('.docapp-gift-choice-modal-gift-price').forEach(function (el) {
      if (el.getAttribute('data-fg-priced')) return;
      el.setAttribute('data-fg-priced', '1');
      el.style.setProperty('color', ACCENT, 'important');
      el.style.setProperty('background', 'transparent', 'important');
    });
  }

  /* ---------- auto-pick the first sample ---------- */

  function selectedCount() {
    var el = document.querySelector('.docapp-gift-choice-modal-counter-selected');
    var n = el ? parseInt((el.textContent || '0').trim(), 10) : 0;
    return isNaN(n) ? 0 : n;
  }
  var autoPicking = false;

  function startAutoPick() {
    if (autoPicking) return;
    autoPicking = true;
    arm();               // stay invisible for the silent pick (already armed in the normal flow)
    attemptClick(0);
  }

  function attemptClick(round) {
    if (!modalSeen || !autoPicking) { return; }
    if (selectedCount() > 0) { finishAutoPick(); return; }    // already registered
    var btn = document.querySelector('.docapp-overlay-modal .docapp-add-to-cart');
    if (btn) { try { btn.click(); } catch (_) {} }
    // Poll ~1.2s for success before risking another click (the app caps at 1, but be gentle).
    var checks = 0;
    (function poll() {
      if (!modalSeen || !autoPicking) { return; }
      if (selectedCount() > 0) { finishAutoPick(); return; }
      if (checks++ < 6) { setTimeout(poll, 200); return; }
      if (round < 2) { attemptClick(round + 1); }            // handler may not have been bound yet
      else { finishAutoPick(); }                             // give up gracefully (unhide/close)
    })();
  }

  function finishAutoPick() {
    setTimeout(function () {
      // Close the popup if the app didn't auto-close it. The ✕ keeps the added gift;
      // NEVER click #docapp-decline-button (that would remove it).
      var modal = document.querySelector('.docapp-overlay-modal');
      if (modal) {
        var close = modal.querySelector('.docapp-overlay-close');
        if (close) { try { close.click(); } catch (_) {} }
      }
      // Guard deliberately stays ARMED here: the pick is silent, and the next
      // automatic open must be invisible too. Only a user action lifts it.
      autoPicking = false;
    }, 500);
  }

  /* ---------- watch the popup ---------- */
  var modalSeen = false;
  function modalIsOpen() {
    var ov = document.querySelector('.docapp-overlay-modal');
    if (!ov) return false;
    // Visible? (don't test opacity: the fg-guard hide uses opacity:0 and must still count as open.)
    var s = getComputedStyle(ov);
    return s.display !== 'none' && s.visibility !== 'hidden';
  }
  function watchModal() {
    var open = modalIsOpen();
    if (open && !modalSeen) { modalSeen = true; onModalOpened(); }
    // Re-arm on close, so the NEXT automatic open is invisible from its first frame.
    else if (!open && modalSeen) { modalSeen = false; autoPicking = false; arm(); }
  }
  function onModalOpened() {
    if (RESTYLE) {
      nudgeSlider();                 // fit the slider to the resized dialog
      enforcePrice();
      [120, 400, 800].forEach(function (d) { setTimeout(enforcePrice, d); });  // catch slow/late renders
    }
    // Reveal decision. Anything that is NOT a silent auto-pick must become visible here — including
    // opens from an app entry point we don't recognise, which is what keeps the guard fail-open.
    var willAutoPick = AUTOPICK && !recentUserOpen() && selectedCount() === 0;
    if (!willAutoPick) { disarm(); return; }
    startAutoPick();
  }

  /* ---------- the in-cart "Changer" link ----------
     The markup is server-rendered by snippets/cart-line-items.liquid on the first sample line, so there is
     nothing to build or re-place here — one delegated listener covers every re-render of the drawer. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-freegift-open]')) { openModal(e); }
  });

  /* ---------- wire up ---------- */
  function init() {
    // Take ownership of the guard from the inline boot script and cancel its self-heal timer.
    try { clearTimeout(window.__fgGuardTimer); } catch (_) {}
    arm();
    watchModal(); // in case the popup is already open on load (this reveals it if it isn't an auto-pick)

    // Light observer (debounced): detect open/close (auto-pick, guard, nudge) and re-apply the price
    // colour to any freshly-rendered spans. No styling here — that's CSS.
    var t = null;
    var mo = new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { watchModal(); enforcePrice(); }, 50);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // A cart change is the moment the app may auto-open its popup (threshold crossed): make sure the
    // guard is armed BEFORE that happens, unless the customer is mid-swap.
    ['theme:cart:change', 'theme:cart:init'].forEach(function (ev) {
      document.addEventListener(ev, function () { if (!recentUserOpen() && !modalSeen) { arm(); } });
    });
  }
  if (document.body) { init(); }
  else { document.addEventListener('DOMContentLoaded', init); }
})();
